import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import AssetsCombobox, { AssetsProvider } from '@/components/ui/AssetsCombobox';
import { DataRetentionRecordUI, fromUI, blankDataRetentionRecordUI } from '../dataTypeMapping';
import { MCMMappingsCard, type MappingLink } from '@/components/mcm/MCMMappingsCard';
import { MCMService } from '@/services/mcmService';
import { useTenancy } from '@/contexts/TenancyContext';
import dataRetentionService from '@/services/dataRetentionService';

interface DataRetentionCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (newRecord: any, linkedSuppliers: string[], linkedAssets: string[]) => void;
  assets: string[];
  suppliers: string[];
}

export const DataRetentionCreateDialog: React.FC<DataRetentionCreateDialogProps> = ({
  open,
  onClose,
  onCreate,
  assets,
  suppliers,
}) => {
  const [form, setForm] = useState<DataRetentionRecordUI>(blankDataRetentionRecordUI());
  const [linkedAssets, setLinkedAssets] = useState<string[]>([]);
  const { activeTenancy } = useTenancy();
  const [controlIds, setControlIds] = useState<string[]>([]);
  const [stagedMappings, setStagedMappings] = useState<Record<string, { links: MappingLink[]; standard?: string }>>({});

  const tempDataRef = React.useMemo(() => `DATA-DRAFT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`, [open]);
  const pendingDataLink = React.useMemo<MappingLink>(() => ({
    id: `internal:datatype_draft:${tempDataRef}`,
    ref: tempDataRef,
    title: form.dataTypeName || 'Data Record',
    domain: 'Data',
    relation: 'relates-to',
  }), [tempDataRef, form.dataTypeName]);

  // Reset form and linkedAssets when dialog is opened
  React.useEffect(() => {
    if (open) {
      setForm(blankDataRetentionRecordUI());
      setLinkedAssets([]);
    }
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    let newValue: any = value;
    if (target.type === 'checkbox') {
      newValue = (target as HTMLInputElement).checked;
    } else if (name === 'dataRetentionPeriodMin' || name === 'dataRetentionPeriodMax') {
      newValue = Number(value);
    }
    setForm(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleCreate = async () => {
    if (!form) return;
    
    // Import getAssets to check componentType
    const { getAssets } = await import('@/services/assetsService');
    const allAssets = await getAssets();
    
    // Separate actual assets from suppliers based on componentType
    const actualAssets: string[] = [];
    const actualSuppliers: string[] = [];
    
    linkedAssets.forEach(assetId => {
      const asset = allAssets.find(a => String(a.assetID) === String(assetId));
      if (asset) {
        if (asset.componentType === 'Supplier') {
          actualSuppliers.push(assetId);
        } else {
          actualAssets.push(assetId);
        }
      }
    });
    
    const entity = fromUI({ ...form, assets: actualAssets, suppliers: actualSuppliers });
    await Promise.resolve(onCreate(entity, actualSuppliers, actualAssets));
    try {
      // Convert staged draft links to real entity link using known ID
      const createdId = String(entity.dataTypeID);
      if (createdId && activeTenancy?.id && Object.keys(stagedMappings).length > 0) {
        const realDataLink: MappingLink = {
          id: `internal:datatype:${createdId}`,
          ref: createdId,
          title: entity.dataTypeName || 'Data Record',
          domain: 'Data',
          relation: 'relates-to',
        };
        const pendingId = pendingDataLink.id;
        const pendingRef = pendingDataLink.ref;
        for (const [controlId, { links, standard }] of Object.entries(stagedMappings)) {
          const newLinks = (links || []).map(l => (l?.id === pendingId || l?.ref === pendingRef) ? { ...realDataLink, title: l?.title || realDataLink.title } : l);
          await MCMService.bulkUpdate({
            controlIds: [String(controlId)],
            type: 'linkedComponents',
            action: 'update',
            data: { linkedComponents: newLinks },
            standard,
          }, activeTenancy.id);
        }
      }
    } finally {
      await dataRetentionService.updateDataType(entity.dataTypeID, entity);
      setStagedMappings({});
      setControlIds([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Create Data Retention Record</DialogTitle>
          </div>
          <div className="flex flex-col gap-4 px-4 pb-4">
            <Label htmlFor="dataTypeName">Type</Label>
            <Input id="dataTypeName" name="dataTypeName" value={form.dataTypeName} onChange={handleChange} />

            <Label htmlFor="dataTypeJustification">Description</Label>
            <Input id="dataTypeJustification" name="dataTypeJustification" value={form.dataTypeJustification} onChange={handleChange} />

            <div className="flex flex-col gap-2">
              <Label>Retention Period</Label>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium">{form.dataRetentionPeriodMin} {form.dataRetentionPeriodUnit}</span>
                <span className="mx-1">–</span>
                <span className="text-sm font-medium">{form.dataRetentionPeriodMax} {form.dataRetentionPeriodUnit}</span>
                <Select
                  value={form.dataRetentionPeriodUnit}
                  onValueChange={unit => setForm(f => ({ ...f, dataRetentionPeriodUnit: unit }))}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">minutes</SelectItem>
                    <SelectItem value="hours">hours</SelectItem>
                    <SelectItem value="days">days</SelectItem>
                    <SelectItem value="weeks">weeks</SelectItem>
                    <SelectItem value="months">months</SelectItem>
                    <SelectItem value="years">years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Slider
                min={0}
                max={100}
                step={1}
                value={[form.dataRetentionPeriodMin, form.dataRetentionPeriodMax]}
                onValueChange={([min, max]) => setForm(f => ({ ...f, dataRetentionPeriodMin: min, dataRetentionPeriodMax: max }))}
                className="w-full"
              />
            </div>

            <Label htmlFor="dataTypeModeOfCollection">Legal Basis</Label>
            <Input id="dataTypeModeOfCollection" name="dataTypeModeOfCollection" value={form.dataTypeModeOfCollection} onChange={handleChange} />

            <div className="flex gap-2 items-center">
              <Label htmlFor="isPersonalInformation">Personal Information?</Label>
              <Checkbox
                id="isPersonalInformation"
                name="isPersonalInformation"
                checked={!!form.isPersonalInformation}
                onCheckedChange={checked => handleChange({ target: { name: 'isPersonalInformation', checked, type: 'checkbox' } } as any)}
              />
            </div>

            <Label htmlFor="status">Status</Label>
            <Select value={form.status} onValueChange={val => setForm(prev => ({ ...prev, status: val as 'Active' | 'Inactive' }))}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Label>Linked Assets & Suppliers</Label>
            <AssetsProvider>
              <AssetsCombobox
                selected={linkedAssets}
                setSelected={setLinkedAssets}
                placeholder="Select assets or suppliers..."
                fullWidth
              />
            </AssetsProvider>
          </div>
          <div className="px-4 pb-4">
            <MCMMappingsCard
              tenancyId={activeTenancy?.id}
              isReadOnly={false}
              controlIds={controlIds}
              persistMode="staged"
              pendingEntityLink={pendingDataLink}
              onEnsureControlLinked={(controlId) => setControlIds(prev => Array.from(new Set([...(prev || []), String(controlId)])))}
              onApplyStaged={({ controlId, links, standard }) => setStagedMappings(prev => ({ ...prev, [controlId]: { links, standard } }))}
              collapsible
            />
          </div>
          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataRetentionCreateDialog;
